import { api } from './api';
import { SearchResult, PaginatedResponse } from '../types';

interface SearchResponse extends PaginatedResponse<SearchResult> {
  facets?: Record<string, Array<{ value: string; count: number }>>;
  searchTime?: number;
  query: string;
  searchType: string;
}

interface SearchSuggestion {
  text: string;
  type: 'phrase' | 'category' | 'tag' | 'document_title';
  score: number;
}

interface SearchFilters {
  categories?: string[];
  tags?: string[];
  fileTypes?: string[];
  dateRange?: {
    start?: string;
    end?: string;
  };
  uploadedBy?: string[];
}

class SearchService {
  // Perform search using the backend /api/v1/files/search endpoint
  async search(params: {
    query: string;
    searchType?: 'basic' | 'semantic' | 'exact';
    filters?: SearchFilters;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    includeFacets?: boolean;
  }): Promise<SearchResponse> {
    // Backend uses GET /api/v1/files/search with query params; the shared
    // client supplies the base path, the bearer token, the refresh and
    // session-expired handling, and the ApiError rejection shape. The private
    // axios instance this used to own bypassed all of them (issue #1096).
    const searchParams = new URLSearchParams({
      q: params.query,
      limit: String(params.pageSize || 20),
      offset: String(((params.page || 1) - 1) * (params.pageSize || 20)),
    });

    // Add category filter if present
    if (params.filters?.categories?.length) {
      searchParams.append('category', params.filters.categories[0]);
    }

    if (params.filters?.dateRange?.start) {
      searchParams.append('date_from', params.filters.dateRange.start);
    }
    if (params.filters?.dateRange?.end) {
      searchParams.append('date_to', params.filters.dateRange.end);
    }

    const data = await api.get<unknown>(`/files/search?${searchParams}`);

    // Backend returns List[SearchResult] directly (array, not object)
    const results: unknown[] = Array.isArray(data)
      ? data
      : ((data as { results?: unknown[] } | null)?.results || []);
    const transformedResults: SearchResult[] = results.map((item: any) => ({
      id: item.id || item.document_id || item.file_id || Math.random().toString(),
      title: item.title || item.file_name || '',
      description: item.snippet || item.description || item.content || '',
      category: item.category || 'design',
      tags: item.tags || [],
      uploadedBy: item.uploaded_by || 'Unknown',
      uploadedAt: item.created_at || item.uploaded_at || new Date().toISOString(),
      mimeType: item.mime_type || 'application/pdf',
      fileSize: item.file_size || 0,
      score: item.relevance_score || item.score || 0,
      highlights: item.highlights,
    }));

    return {
      results: transformedResults,
      total: transformedResults.length,
      page: params.page || 1,
      pageSize: params.pageSize || 20,
      totalPages: Math.ceil(transformedResults.length / (params.pageSize || 20)),
      facets: undefined,
      query: params.query,
      searchType: params.searchType || 'basic',
    };
  }

  // ADR-005: Stub methods — backend endpoints not implemented.
  // Kept as no-op stubs because callers (SearchBar, SearchPage) reference them.
  // TODO(Sprint 2+): Implement or remove callers.

  async getSuggestions(_query: string, _limit: number = 10): Promise<SearchSuggestion[]> {
    return [];
  }

  async getAutocomplete(_query: string, _field?: string): Promise<string[]> {
    return [];
  }

  async trackSearch(_searchEvent: {
    query: string;
    searchType: string;
    resultsCount: number;
    filters?: SearchFilters;
    clickedResults?: string[];
    sessionId?: string;
  }): Promise<void> {
    // No-op: analytics endpoint not yet implemented
  }

  async trackClick(_clickEvent: {
    searchId?: string;
    documentId: string;
    resultPosition: number;
    query: string;
  }): Promise<void> {
    // No-op: click tracking endpoint not yet implemented
  }

  async getPopularSearches(_timeRange?: 'day' | 'week' | 'month'): Promise<
    Array<{ query: string; count: number }>
  > {
    return [];
  }

  async vectorSearch(_params: {
    query: string;
    topK?: number;
    filters?: SearchFilters;
    threshold?: number;
  }): Promise<SearchResponse> {
    // ADR-005: vector search will be routed through RetrievalService
    throw new Error('Vector search not yet available — use standard search');
  }

  async hybridSearch(_params: {
    query: string;
    alpha?: number;
    filters?: SearchFilters;
    page?: number;
    pageSize?: number;
  }): Promise<SearchResponse> {
    // ADR-005: hybrid search will be routed through RetrievalService
    throw new Error('Hybrid search not yet available — use standard search');
  }
}

export const searchService = new SearchService();
export default searchService;
